import { FormatNumberHelper } from '@core/helpers/format-number.helper';
import { MeasureHelper } from '@core/helpers/measure.helper';
import { Threshold } from '@core/models/threshold.model';
import { BlockChartData } from '../models/chart-data.model';
import { BlockError, BlockGraph, ClosedPath, GraphConfig } from '../models/graph.model';
import { BlockMetric, ExtendedMetric } from '../models/metric.model';
import { PathEquipment } from '../models/path-element.model';
import { PathGasBlock } from '../models/path-gas-block.model';
import { UnitHelper } from '@core/helpers/unit.helper';

export class DataChartHelper {
  public static generateTitle(chartData: BlockChartData): string {
    const graph = chartData.graphs[0];
    const pathLength = graph.length;
    const pathStart = graph.equipments[0]?.name || '';
    const pathEnd = graph.equipments[graph.equipments.length - 1]?.name || '';
    const pathPipes = [...new Set(graph.pipes.map(x => x.name))].length; // use Set to count unique pipe names
    return `COURBE DE LIEU de ${pathStart} à ${pathEnd} - ${pathLength} km - ${pathPipes} tuyau${
      pathPipes > 1 ? 'x' : ''
    }`;
  }

  public static formatChartData = (
    properties: Array<Threshold>,
    pathKeys: Array<string>,
    elements: Array<PathEquipment>,
    gasBlocks: Array<PathGasBlock>,
    graphConfig: GraphConfig,
    closedPaths: Array<ClosedPath>,
    timestep?: string
  ): BlockChartData => {
    // 3 stages measure for: 1 each property request, 2 each pipe / equipment, 3 each measure
    const mesures: Array<Array<Array<ExtendedMetric>>> = [...properties.map(property => [])];
    const legendGraph = {
      tag: properties[0].name,
      property: properties[0],
      metrics: [],
      color: '#FFFFFF',
      equipments: [],
      pipes: [],
      length: 0
    } as BlockGraph;
    // generate 6 graph curve for each property (line ok, dot ok, line out, dot out, leftBracket, rightBracket)
    const lineGraphs = DataChartHelper.generateBlockGraph(properties, graphConfig);
    const dotGraphs = DataChartHelper.generateBlockGraph(properties, graphConfig);
    const lineOutGraphs = DataChartHelper.generateBlockGraph(properties, graphConfig, true);
    const dotOutGraphs = DataChartHelper.generateBlockGraph(properties, graphConfig, true);
    const blockedLeftGraphs = DataChartHelper.generateBlockError(properties);
    const blockedRightGraphs = DataChartHelper.generateBlockError(properties);

    // build point according to strategy
    if (graphConfig.isBlock) {
      let start = 0;
      let end = 0;
      let lastValues = [];
      gasBlocks.forEach(block => {
        // refresh the start of the pipe at each new pipe
        if (block.upstreamPosition === 0) {
          start = end;
          properties.forEach((prop, p) => {
            mesures[p].push([]);
          });
        }
        // store the end length and value to build the last point of the graph
        end = start + block.downstreamPosition;
        lastValues = block.measures;
        // build the extended points if value
        if (lastValues && mesures.length > 0) {
          // build the extended points
          properties.forEach((prop, p) => {
            mesures[p][mesures[p].length - 1].push({
              id: block.gasBlockId,
              value: Math.abs(+UnitHelper.convert(lastValues[p], prop.name)),
              length: +((block.downstreamPosition - block.upstreamPosition) / 1000).toFixed(4),
              start: (start + block.upstreamPosition) / 1000,
              end: end / 1000
            });
          });
          legendGraph.pipes.push({
            id: block.gasBlockId,
            name: block.name,
            ouvrage: block.ouvrage,
            length: +((start + block.upstreamPosition) / 1000).toFixed(4)
          });
          legendGraph.pipes.push({
            id: block.gasBlockId,
            name: block.name,
            ouvrage: block.ouvrage,
            length: +((start + block.upstreamPosition + end) / 2000).toFixed(4)
          });
          legendGraph.pipes.push({
            id: block.gasBlockId,
            name: block.name,
            ouvrage: block.ouvrage,
            length: +(end / 1000).toFixed(4)
          });
        }
      });
    } else {
      let position = 0;
      elements.forEach((element, e) => {
        if (e % 2 === 1 && element.measures) {
          properties.forEach((prop, p) => {
            mesures[p].push([
              {
                id: element.name,
                value: Math.abs(+UnitHelper.convert(element.measures[p], prop.name)),
                length: element.length / 1000,
                start: position,
                end: position + element.length / 1000
              }
            ]);
          });
        }
        position += element.length / 1000;
      });
    }

    let min = null;
    let max = null;
    // if display block, we have to add null values to disable vertical lines
    properties.forEach((prop, p) => {
      const blocks: Array<BlockMetric> = [];
      let lastId;
      mesures[p].forEach(pipe => {
        pipe.forEach(mesure => {
          if (lastId) {
            blocks.push({
              value: NaN,
              length: +mesure.start.toFixed(4)
            });
          }
          // start of the step
          blocks.push({
            //value: UnitHelper.convert(mesure.value, prop.name),
            value: mesure.value,
            length: +mesure.start.toFixed(4)
          });
          // middle of the step to display tooltip
          blocks.push({
            value: mesure.value,
            length: +((mesure.start + mesure.end) / 2).toFixed(4)
          });
          // end of the step
          blocks.push({
            value: mesure.value,
            length: +mesure.end.toFixed(4)
          });
          // only compute min/max for primary axis (based on unit)
          if (prop.unit === legendGraph.property.unit && FormatNumberHelper.isValidNumber(mesure.value)) {
            min = FormatNumberHelper.isValidNumber(min) ? Math.min(min, mesure.value) : mesure.value;
            max = FormatNumberHelper.isValidNumber(max) ? Math.max(max, mesure.value) : mesure.value;
          }
          lastId = mesure.id;
        });
      });
      lineGraphs[p].metrics = blocks;
    });

    // setup points, equipment and pipe properties
    let position = 0;
    let lastPathIndex = -1;
    elements.forEach((element, e) => {
      const lastPosition = position + element.length / 2000;
      position += element.length / 1000;
      if (e % 2 === 0) {
        // fill points
        properties.forEach((prop, p) => {
          if (element.measures[p]) {
            const value = Math.abs(+UnitHelper.convert(element.measures[p], prop.name));
            dotGraphs[p].metrics.push({
              value,
              length: +lastPosition.toFixed(4)
            });
            if (prop.unit === legendGraph.property.unit && FormatNumberHelper.isValidNumber(value)) {
              min = FormatNumberHelper.isValidNumber(min) ? Math.min(min, value) : value;
              max = FormatNumberHelper.isValidNumber(max) ? Math.max(max, value) : value;
            }
          }
        });
        // fill equipments
        const pathIndex = pathKeys.findIndex(key => key === element.key);
        legendGraph.equipments.push({
          keys: pathKeys.filter((key, k) => k <= pathIndex && k > lastPathIndex),
          name: element.name,
          ouvrage: element.ouvrage,
          length: +position.toFixed(4)
        });
        lastPathIndex = pathIndex;
      } else {
        // fill pipes
        if (!graphConfig.isBlock) {
          legendGraph.pipes.push({
            name: element.name,
            ouvrage: element.ouvrage,
            length: +lastPosition.toFixed(4)
          });
        }
      }
    });

    // generate another dataset to be use as overable labels
    //const legendValue = max > min ? min - (max - min) / 10 : min === 0 ? -1 : 0;
    //dotGraph.metrics.forEach(metric => {
    let legendPosition = 0;
    elements.forEach((element, e) => {
      legendPosition += element.length / 1000;
      if (e % 2 === 0) {
        legendGraph.metrics.push({
          value: min || 0,
          length: +legendPosition.toFixed(4)
        });
      }
    });
    //});
    legendGraph.length = +position.toFixed(4);

    // generate missing / closed object curve
    closedPaths.forEach(closedPath => {
      const equipment = legendGraph.equipments.find(equip => equip.keys.includes(closedPath.key));
      if (equipment) {
        equipment.closed = true;
        lineGraphs.forEach((lineGraph, index) => {
          const measures = lineGraph.metrics.filter(metric => metric.length === equipment.length);
          if (measures.length) {
            blockedLeftGraphs[index].metrics.push({
              value: measures[0].value,
              length: +measures[0].length.toFixed(4)
            });
            blockedRightGraphs[index].metrics.push({
              value: measures[measures.length - 1].value,
              length: +measures[0].length.toFixed(4)
            });
            const closedEquipment = {
              key: closedPath.key,
              name: closedPath.name,
              closed: closedPath.closed,
              length: +measures[0].length.toFixed(4)
            } as ClosedPath;
            blockedLeftGraphs[index].equipments.push(closedEquipment);
            blockedRightGraphs[index].equipments.push(closedEquipment);
          }
        });
      }
    });

    // split line and dot to 2 different curve according to tollerances
    lineGraphs.forEach((curve, c) => {
      const lowLimit = UnitHelper.convert(properties[c].lowLimit, curve.tag);
      const highLimit = UnitHelper.convert(properties[c].highLimit, curve.tag);
      lineGraphs[c].metrics.forEach((metric, m) => {
        const isInRange =
          (MeasureHelper.isNaN(lowLimit) || metric.value > lowLimit) &&
          (MeasureHelper.isNaN(highLimit) || metric.value < highLimit);
        lineOutGraphs[c].metrics.push({
          length: metric.length,
          value: isInRange ? NaN : metric.value
        });
      });
    });
    dotGraphs.forEach((curve, c) => {
      const lowLimit = UnitHelper.convert(properties[c].lowLimit, curve.tag);
      const highLimit = UnitHelper.convert(properties[c].highLimit, curve.tag);
      dotGraphs[c].metrics.forEach((metric, m) => {
        const isInRange =
          (MeasureHelper.isNaN(lowLimit) || metric.value > lowLimit) &&
          (MeasureHelper.isNaN(highLimit) || metric.value < highLimit);
        dotOutGraphs[c].metrics.push({
          length: metric.length,
          value: isInRange ? NaN : metric.value
        });
      });
    });

    return {
      graphs: [
        legendGraph,
        ...DataChartHelper.orderGraphData(
          lineGraphs,
          dotGraphs,
          lineOutGraphs,
          dotOutGraphs,
          blockedLeftGraphs,
          blockedRightGraphs,
          properties.length
        )
      ],
      // use ...new Set() to avoid duplicates
      axes: [...new Set(properties.map(prop => UnitHelper.getPropertyUnit(prop.name)))].map(unit => {
        return {
          axeId: unit,
          scale: { marginPercent: 2 }
        };
      }),
      timestep: timestep || ''
    } as BlockChartData;
  };

  public static orderGraphData = (
    lineGraphs: Array<BlockGraph>,
    dotGraphs: Array<BlockGraph>,
    lineOutGraphs: Array<BlockGraph>,
    dotOutGraphs: Array<BlockGraph>,
    blockedLeftGraphs: Array<BlockError>,
    blockedRightGraphs: Array<BlockError>,
    length: number
  ): Array<BlockGraph | BlockError> => {
    const graphArray = [];
    for (let i = 0; i < length; i++) {
      graphArray.push(lineGraphs[i]);
      graphArray.push(dotGraphs[i]);
      graphArray.push(lineOutGraphs[i]);
      graphArray.push(dotOutGraphs[i]);
      graphArray.push(blockedLeftGraphs[i]);
      graphArray.push(blockedRightGraphs[i]);
    }
    return graphArray;
  };

  public static generateBlockGraph = (
    properties: Array<Threshold>,
    config: GraphConfig,
    out?: boolean
  ): Array<BlockGraph> => {
    return properties.map((property, p) => {
      return {
        tag: property.name,
        property: { ...property, unit: UnitHelper.getPropertyUnit(property.name) },
        metrics: [],
        color: out ? '#F2142E' : DataChartHelper.getChartColor(config, p),
        equipments: [],
        pipes: [],
        length: 0
      } as BlockGraph;
    });
  };

  public static generateBlockError = (properties: Array<Threshold>): Array<BlockError> => {
    return properties.map((property, p) => {
      return {
        tag: property.name,
        property: { ...property, unit: UnitHelper.getPropertyUnit(property.name) },
        metrics: [],
        color: '#F2142E',
        equipments: [],
        length: 0
      } as BlockError;
    });
  };

  public static getChartColor(config: GraphConfig, index: number): string {
    // colorList: '#F5FF02','#72BE44', '#00A2DD'
    return config.colors[index] || '#FFFFFF';
  }
}
